import React, { useMemo } from 'react';
import { orderedServiceLangs } from '../utils/serviceNameLangs';
import './BandeauDefilant.css';

/**
 * Bandelette DÉFILANTE de la borne — une ligne de message, réglée au central.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Ce que l'exploitant saisit une fois en console s'affiche sur toutes les bornes du site.
 * Le message vit dans `config.distributor.ticker` — donc dans la configuration du SERVEUR
 * et non dans celle de chaque borne : un texte lu par le public ne se saisit pas poste par
 * poste, sous peine de dire deux choses différentes dans la même salle.
 *
 * LES LANGUES SUIVENT CELLES DÉJÀ RÉGLÉES pour les noms de service (`orderedServiceLangs`),
 * dans le même ordre. Rien de nouveau à régler : une borne trilingue fait défiler les trois
 * versions à la suite, une borne arabophone n'en fait défiler qu'une. Chaque version porte
 * SA direction d'écriture (`dir`) — l'arabe ne se façonne pas correctement dans un conteneur
 * latin, et une phrase arabe mélangée à une phrase française dans un même `dir` sort mal
 * découpée.
 *
 * LE SENS DE DÉFILEMENT EST UN RÉGLAGE À PART, et c'est délibéré : il relève de la mise en
 * scène, pas de la langue. Un exploitant arabophone peut préférer un défilement de gauche à
 * droite parce que c'est ce à quoi son public est habitué sur les chaînes d'information.
 *
 * PAS DE REPLI « MOUVEMENT RÉDUIT » : couper l'animation rendrait inaccessible tout ce qui
 * dépasse la largeur de l'écran, c'est-à-dire l'essentiel d'un message un peu long. Le texte
 * complet est présent UNE fois dans le DOM pour les lecteurs d'écran ; la seconde copie, qui
 * ne sert qu'à faire la boucle sans saut, est masquée (`aria-hidden`).
 */
const BandeauDefilant = ({ config, position }) => {
  const reglage = config?.distributor?.ticker || {};

  /**
   * Les morceaux à faire défiler, un par langue active et non vide.
   *
   * Une langue activée mais laissée vide est SAUTÉE plutôt que rendue comme un blanc : sans
   * cela, une borne trilingue dont seul l'arabe est renseigné ferait défiler un long vide
   * entre deux passages, que l'on prendrait pour une panne d'affichage.
   */
  const morceaux = useMemo(() => {
    const msg = reglage.message || {};
    // Même appel que la grille des services, juste au-dessus : l'ordre et la visibilité des
    // langues sont ceux déjà réglés dans « Styles → Affichage ». Le préfixe de classes ne
    // sert pas ici — seules la clé de langue et sa direction d'écriture nous intéressent.
    return orderedServiceLangs(config?.styles?.display, 'bandelette')
      .map((l) => ({ lang: l.key, dir: l.dir, texte: String(msg[l.key] ?? '').trim() }))
      .filter((m) => m.texte);
  }, [config, reglage.message]);

  // Désactivée, ou rien à dire : on ne rend RIEN. Une bandelette vide occuperait une ligne
  // de la borne — au détriment des boutons de service, qui sont la raison d'être de l'écran.
  if (reglage.enabled !== true || !morceaux.length) return null;

  /**
   * EMPLACEMENT. La borne pose la bandelette à deux endroits possibles et laisse le composant
   * décider lequel s'affiche : c'est le réglage qui tranche, pas le gabarit de la page.
   * Sans `position` (l'aperçu de la page de réglage), on rend toujours.
   */
  const emplacement = reglage.position === 'bottom' ? 'bottom' : 'top';
  if (position && position !== emplacement) return null;

  const duree = Number(reglage.duration) > 0 ? Number(reglage.duration) : 20;
  const sens = reglage.direction === 'ltr' ? 'ltr' : 'rtl';

  /**
   * L'APPARENCE passe par des variables CSS posées en ligne, et non par des styles inline sur
   * chaque élément : la feuille garde ainsi la main sur la mise en page — ce qui relève du
   * composant — pendant que la configuration ne pilote que les couleurs, la police et la
   * taille, qui relèvent de l'exploitant.
   *
   * Une valeur absente n'est PAS posée : la variable garde alors le défaut de la feuille. Poser
   * `undefined` reviendrait à écrire une variable vide, qui écrase le défaut par du vide.
   */
  const style = {};
  if (reglage.color) style['--bandelette-encre'] = reglage.color;
  if (reglage.backgroundColor) style['--bandelette-fond'] = reglage.backgroundColor;
  if (reglage.separatorColor) style['--bandelette-accent'] = reglage.separatorColor;
  // Police vide = celle de la borne. C'est la convention des thèmes, et elle évite qu'un site
  // ayant choisi sa police doive la redire ici.
  if (reglage.fontFamily) style['--bandelette-police'] = reglage.fontFamily;
  if (Number(reglage.fontSize) > 0) style['--bandelette-taille'] = `${Number(reglage.fontSize)}rem`;
  style['--bandelette-graisse'] = reglage.bold === false ? '400' : '700';

  const suite = (cle) => (
    <span className="bandelette-suite" key={cle}>
      {morceaux.map((m, i) => (
        <React.Fragment key={`${cle}-${m.lang}`}>
          {i > 0 && <span className="bandelette-separateur" aria-hidden="true">◆</span>}
          <span className="bandelette-texte" dir={m.dir || 'ltr'} lang={m.lang}>
            {m.texte}
          </span>
        </React.Fragment>
      ))}
    </span>
  );

  return (
    <div className="bandelette" data-sens={sens} data-position={emplacement} style={style}>
      <div
        className="bandelette-piste"
        style={{ animationDuration: `${duree}s` }}
      >
        {suite('lu')}
        {/* Seconde copie : elle seule permet à la boucle de se refermer sans saut visible.
            Masquée aux lecteurs d'écran, qui liraient sinon le message deux fois. */}
        <span aria-hidden="true">{suite('boucle')}</span>
      </div>
    </div>
  );
};

export default BandeauDefilant;
